/**
 * AI Agent Dashboard - Admin interface for managing AI support agent configuration
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  FileText,
  MessageSquare,
  AlertTriangle,
  BarChart3,
  Plus,
  Edit2,
  Trash2,
  Save,
  CheckCircle,
  XCircle,
  Loader2,
  TestTube,
  Settings,
} from 'lucide-react';
import { AgentSettingsTab } from '../../components/admin/ai-agent/AgentSettingsTab';
import { api } from '../../services/api';

type TabKey = 'settings' | 'rules' | 'prompts' | 'templates' | 'escalation' | 'metrics';

interface AIRule {
  id: string;
  rule_type: 'behavior' | 'response' | 'restriction' | 'escalation';
  title: string;
  content: string;
  priority: number;
  is_active: boolean;
}

interface AIPrompt {
  id: string;
  name: string;
  content: string;
  version: number;
  is_active: boolean;
}

interface AITemplate {
  id: string;
  name: string;
  category: string;
  trigger_keywords: string[];
  content: string;
  variables: string[];
  is_active: boolean;
}

interface EscalationRule {
  id: string;
  name: string;
  rule_type: 'confidence' | 'keyword' | 'category' | 'sentiment';
  config: Record<string, unknown>;
  notify_email: string | null;
  priority: number;
  is_active: boolean;
}

interface MetricsSummary {
  total_tickets: number;
  resolved_tickets: number;
  escalated_tickets: number;
  resolution_rate: number;
  escalation_rate: number;
  avg_confidence: number;
  avg_response_time_ms: number;
  satisfaction_positive: number;
  satisfaction_negative: number;
  satisfaction_rate: number;
  top_escalation_reasons: Array<{ reason: string; count: number }>;
}

const TABS: { key: TabKey; label: string; icon: typeof Bot }[] = [
  { key: 'settings', label: 'Agent Settings', icon: Settings },
  { key: 'rules', label: 'Rules', icon: FileText },
  { key: 'prompts', label: 'Prompts', icon: MessageSquare },
  { key: 'templates', label: 'Templates', icon: Bot },
  { key: 'escalation', label: 'Escalation Config', icon: AlertTriangle },
  { key: 'metrics', label: 'Metrics', icon: BarChart3 },
];

const RULE_TYPES = [
  { value: 'behavior', label: 'Behavior', color: 'bg-blue-100 text-blue-700' },
  { value: 'response', label: 'Response', color: 'bg-green-100 text-green-700' },
  { value: 'restriction', label: 'Restriction', color: 'bg-red-100 text-red-700' },
  { value: 'escalation', label: 'Escalation', color: 'bg-amber-100 text-amber-700' },
];

const ESCALATION_TYPES = [
  { value: 'confidence', label: 'Confidence Threshold' },
  { value: 'keyword', label: 'Keyword Match' },
  { value: 'category', label: 'Category Match' },
  { value: 'sentiment', label: 'Sentiment Analysis' },
];

export default function AIAgentDashboard() {
  const [activeTab, setActiveTab] = useState<TabKey>('settings');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-7 h-7 text-primary-600" />
            AI Support Agent
          </h1>
          <p className="text-gray-600 mt-1">
            Configure rules, prompts, and behavior for the AI support agent.
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      isActive
                        ? 'border-primary-600 text-primary-600 bg-primary-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'settings' && <AgentSettingsTab />}
            {activeTab === 'rules' && <RulesTab />}
            {activeTab === 'prompts' && <PromptsTab />}
            {activeTab === 'templates' && <TemplatesTab />}
            {activeTab === 'escalation' && <EscalationTab />}
            {activeTab === 'metrics' && <MetricsTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

// =======================
// RULES TAB
// =======================

function RulesTab() {
  const queryClient = useQueryClient();
  const [editingRule, setEditingRule] = useState<AIRule | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-agent-rules'],
    queryFn: () => api.getAIAgentRules(),
  });

  const createMutation = useMutation({
    mutationFn: (rule: Omit<AIRule, 'id'>) => api.createAIAgentRule(rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-rules'] });
      setIsCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (rule: AIRule) => api.updateAIAgentRule(rule.id, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-rules'] });
      setEditingRule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAIAgentRule(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-agent-rules'] }),
  });

  const rules = data?.rules || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Define rules and guidelines that the AI agent must follow when responding to customers.
        </p>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Rule
        </button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <RuleForm
          onSubmit={(rule) => createMutation.mutate(rule)}
          onCancel={() => setIsCreating(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Rules List */}
      <div className="space-y-3">
        {rules.map((rule: AIRule) => (
          <div
            key={rule.id}
            className={`border rounded-lg p-4 ${rule.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}
          >
            {editingRule?.id === rule.id ? (
              <RuleForm
                rule={editingRule}
                onSubmit={(r) => updateMutation.mutate({ ...r, id: rule.id })}
                onCancel={() => setEditingRule(null)}
                isLoading={updateMutation.isPending}
              />
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${RULE_TYPES.find(t => t.value === rule.rule_type)?.color}`}>
                      {RULE_TYPES.find(t => t.value === rule.rule_type)?.label}
                    </span>
                    <span className="text-xs text-gray-400">Priority: {rule.priority}</span>
                    {!rule.is_active && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                        Inactive
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-900">{rule.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{rule.content}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingRule(rule)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Delete this rule?')) {
                        deleteMutation.mutate(rule.id);
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RuleForm({
  rule,
  onSubmit,
  onCancel,
  isLoading,
}: {
  rule?: AIRule;
  onSubmit: (rule: Omit<AIRule, 'id'>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState({
    rule_type: rule?.rule_type || 'behavior',
    title: rule?.title || '',
    content: rule?.content || '',
    priority: rule?.priority ?? 50,
    is_active: rule?.is_active ?? true,
  });

  return (
    <div className="border border-primary-200 rounded-lg p-4 bg-primary-50/30">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="rule-form-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            id="rule-form-type"
            value={form.rule_type}
            onChange={(e) => setForm({ ...form, rule_type: e.target.value as AIRule['rule_type'] })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          >
            {RULE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rule-form-priority" className="block text-sm font-medium text-gray-700 mb-1">Priority (0-100)</label>
          <input
            id="rule-form-priority"
            type="number"
            min="0"
            max="100"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="rule-form-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            id="rule-form-title"
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Brief title for the rule"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="rule-form-content" className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <textarea
            id="rule-form-content"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="The rule or guideline the AI must follow..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent resize-none"
          />
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="is_active" className="text-sm text-gray-700">
            Active
          </label>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() => onSubmit(form as Omit<AIRule, 'id'>)}
          disabled={isLoading || !form.title || !form.content}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =======================
// PROMPTS TAB
// =======================

function PromptsTab() {
  const queryClient = useQueryClient();
  const [editingPrompt, setEditingPrompt] = useState<AIPrompt | null>(null);
  interface TestResult {
    system_prompt_length: number;
    matching_templates?: { name: string }[];
    escalation_check: {
      shouldEscalate: boolean;
      triggeredRules: string[];
    } | null;
  }
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-agent-prompts'],
    queryFn: () => api.getAIAgentPrompts(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ name, content }: { name: string; content: string }) =>
      api.updateAIAgentPrompt(name, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-prompts'] });
      setEditingPrompt(null);
    },
  });

  const testMutation = useMutation({
    mutationFn: (message: string) => api.testAIAgent({ message }),
    onSuccess: (result) => setTestResult(result),
  });

  const prompts = data?.prompts || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Manage system prompts that define the AI agent's persona and behavior.
      </p>

      {/* Prompts List */}
      <div className="space-y-4">
        {prompts.map((prompt: AIPrompt) => (
          <div key={prompt.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-gray-900">{prompt.name}</span>
                <span className="text-xs text-gray-400">v{prompt.version}</span>
              </div>
              <button
                onClick={() => setEditingPrompt(prompt)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            </div>
            {editingPrompt?.id === prompt.id ? (
              <div className="p-4">
                <textarea
                  value={editingPrompt.content}
                  onChange={(e) => setEditingPrompt({ ...editingPrompt, content: e.target.value })}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                />
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => updateMutation.mutate({ name: prompt.name, content: editingPrompt.content })}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save
                  </button>
                  <button
                    onClick={() => setEditingPrompt(null)}
                    className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4">
                <pre className="text-sm text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 p-3 rounded-lg max-h-48 overflow-y-auto">
                  {prompt.content}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Test Panel */}
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
          <TestTube className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-900">Test AI Agent</span>
        </div>
        <div className="p-4 space-y-3">
          <textarea
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Enter a test message to see how the AI would respond..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
          <button
            onClick={() => testMutation.mutate(testMessage)}
            disabled={testMutation.isPending || !testMessage.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {testMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
            Test
          </button>
          {testResult && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm">
              <p className="text-gray-500 mb-2">System prompt length: {testResult.system_prompt_length} chars</p>
              {testResult.matching_templates?.length && testResult.matching_templates.length > 0 && (
                <div className="mb-2">
                  <span className="text-gray-500">Matched templates: </span>
                  {testResult.matching_templates.map((t) => t.name).join(', ')}
                </div>
              )}
              {testResult.escalation_check && (
                <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200">
                  <p className="text-amber-700 text-xs">
                    Escalation: {testResult.escalation_check.shouldEscalate ? 'YES' : 'No'}
                    {testResult.escalation_check.triggeredRules.length > 0 && (
                      <> - Rules: {testResult.escalation_check.triggeredRules.join(', ')}</>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =======================
// TEMPLATES TAB
// =======================

function TemplatesTab() {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AITemplate | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-agent-templates'],
    queryFn: () => api.getAIAgentTemplates(),
  });

  const createMutation = useMutation({
    mutationFn: (template: Omit<AITemplate, 'id'>) => api.createAIAgentTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-templates'] });
      setIsCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (template: AITemplate) => api.updateAIAgentTemplate(template.id, template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-templates'] });
      setEditingTemplate(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAIAgentTemplate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-agent-templates'] }),
  });

  const templates = data?.templates || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Response templates with trigger keywords for common questions.
        </p>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Template
        </button>
      </div>

      {isCreating && (
        <TemplateForm
          onSubmit={(t) => createMutation.mutate(t)}
          onCancel={() => setIsCreating(false)}
          isLoading={createMutation.isPending}
        />
      )}

      <div className="space-y-3">
        {templates.map((template: AITemplate) => (
          <div key={template.id} className="border border-gray-200 rounded-lg bg-white p-4">
            {editingTemplate?.id === template.id ? (
              <TemplateForm
                template={editingTemplate}
                onSubmit={(t) => updateMutation.mutate({ ...t, id: template.id })}
                onCancel={() => setEditingTemplate(null)}
                isLoading={updateMutation.isPending}
              />
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{template.name}</span>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                      {template.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{template.content}</p>
                  {template.trigger_keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {template.trigger_keywords.map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-primary-50 text-primary-700 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingTemplate(template)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => confirm('Delete this template?') && deleteMutation.mutate(template.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateForm({
  template,
  onSubmit,
  onCancel,
  isLoading,
}: {
  template?: AITemplate;
  onSubmit: (t: Omit<AITemplate, 'id'>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState({
    name: template?.name || '',
    category: template?.category || 'general',
    trigger_keywords: template?.trigger_keywords?.join(', ') || '',
    content: template?.content || '',
    variables: template?.variables || [],
    is_active: template?.is_active ?? true,
  });

  return (
    <div className="border border-primary-200 rounded-lg p-4 bg-primary-50/30">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="template-form-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            id="template-form-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="template-form-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <input
            id="template-form-category"
            type="text"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="template-form-keywords" className="block text-sm font-medium text-gray-700 mb-1">Trigger Keywords (comma-separated)</label>
          <input
            id="template-form-keywords"
            type="text"
            value={form.trigger_keywords}
            onChange={(e) => setForm({ ...form, trigger_keywords: e.target.value })}
            placeholder="cancel, subscription, refund"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="template-form-content" className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <textarea
            id="template-form-content"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent resize-none"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() =>
            onSubmit({
              ...form,
              trigger_keywords: form.trigger_keywords.split(',').map((k) => k.trim()).filter(Boolean),
            })
          }
          disabled={isLoading || !form.name || !form.content}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100">
          Cancel
        </button>
      </div>
    </div>
  );
}

// =======================
// ESCALATION TAB
// =======================

function EscalationTab() {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingRule, setEditingRule] = useState<EscalationRule | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-escalation-rules'],
    queryFn: () => api.getAIEscalationRules(),
  });

  const createMutation = useMutation({
    mutationFn: (rule: {
      name: string;
      rule_type: string;
      config: Record<string, unknown>;
      notify_email?: string;
      priority?: number;
      is_active?: boolean;
    }) => api.createAIEscalationRule(rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-escalation-rules'] });
      setIsCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...rule }: EscalationRule) => api.updateAIEscalationRule(id, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-escalation-rules'] });
      setEditingRule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAIEscalationRule(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-escalation-rules'] }),
  });

  const rules = data?.rules || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Configure when tickets should be escalated to human support.
        </p>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Rule
        </button>
      </div>

      {isCreating && (
        <EscalationForm
          onSubmit={(r) => createMutation.mutate({
            name: r.name,
            rule_type: r.rule_type,
            config: r.config,
            notify_email: r.notify_email || undefined,
            priority: r.priority,
            is_active: r.is_active,
          })}
          onCancel={() => setIsCreating(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {editingRule && (
        <EscalationForm
          rule={editingRule}
          onSubmit={(r) => updateMutation.mutate({
            id: editingRule.id,
            name: r.name,
            rule_type: r.rule_type,
            config: r.config,
            notify_email: r.notify_email,
            priority: r.priority,
            is_active: r.is_active,
          })}
          onCancel={() => setEditingRule(null)}
          isLoading={updateMutation.isPending}
        />
      )}

      <div className="space-y-3">
        {rules.map((rule: EscalationRule) => (
          <div key={rule.id} className="border border-gray-200 rounded-lg bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">{rule.name}</span>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                    {ESCALATION_TYPES.find((t) => t.value === rule.rule_type)?.label}
                  </span>
                  <span className="text-xs text-gray-400">Priority: {rule.priority}</span>
                </div>
                <pre className="text-sm text-gray-600 mt-1 bg-gray-50 p-2 rounded">
                  {JSON.stringify(rule.config, null, 2)}
                </pre>
                {rule.notify_email && (
                  <p className="text-xs text-gray-500 mt-2">Notify: {rule.notify_email}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingRule(rule)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => confirm('Delete this rule?') && deleteMutation.mutate(rule.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EscalationForm({
  rule,
  onSubmit,
  onCancel,
  isLoading,
}: {
  rule?: EscalationRule;
  onSubmit: (r: Omit<EscalationRule, 'id'>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState({
    name: rule?.name || '',
    rule_type: rule?.rule_type || 'confidence',
    config: JSON.stringify(rule?.config || { threshold: 0.6, action: 'escalate' }, null, 2),
    notify_email: rule?.notify_email || 'stephen@rumio.world',
    priority: rule?.priority ?? 50,
    is_active: rule?.is_active ?? true,
  });

  return (
    <div className="border border-primary-200 rounded-lg p-4 bg-primary-50/30">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="escalation-form-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            id="escalation-form-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="escalation-form-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            id="escalation-form-type"
            value={form.rule_type}
            onChange={(e) => setForm({ ...form, rule_type: e.target.value as EscalationRule['rule_type'] })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          >
            {ESCALATION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="escalation-form-email" className="block text-sm font-medium text-gray-700 mb-1">Notify Email</label>
          <input
            id="escalation-form-email"
            type="email"
            value={form.notify_email}
            onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="escalation-form-priority" className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <input
            id="escalation-form-priority"
            type="number"
            min="0"
            max="100"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="escalation-form-config" className="block text-sm font-medium text-gray-700 mb-1">Config (JSON)</label>
          <textarea
            id="escalation-form-config"
            value={form.config}
            onChange={(e) => setForm({ ...form, config: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-600 focus:border-transparent resize-none"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() => {
            try {
              const config = JSON.parse(form.config);
              onSubmit({ ...form, config } as Omit<EscalationRule, 'id'>);
            } catch {
              alert('Invalid JSON in config');
            }
          }}
          disabled={isLoading || !form.name}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100">
          Cancel
        </button>
      </div>
    </div>
  );
}

// =======================
// METRICS TAB
// =======================

function MetricsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-agent-metrics-summary'],
    queryFn: () => api.getAIAgentMetricsSummary(),
  });

  const summary: MetricsSummary | undefined = data?.summary;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!summary || summary.total_tickets === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No Data Yet</h3>
        <p className="text-gray-500 mt-1">Metrics will appear here once the AI agent starts handling tickets.</p>
      </div>
    );
  }

  const stats = [
    { label: 'Total Tickets', value: summary.total_tickets, color: 'bg-blue-100 text-blue-700' },
    { label: 'Resolved', value: summary.resolved_tickets, color: 'bg-green-100 text-green-700' },
    { label: 'Escalated', value: summary.escalated_tickets, color: 'bg-amber-100 text-amber-700' },
    { label: 'Resolution Rate', value: `${(summary.resolution_rate * 100).toFixed(1)}%`, color: 'bg-green-100 text-green-700' },
    { label: 'Escalation Rate', value: `${(summary.escalation_rate * 100).toFixed(1)}%`, color: 'bg-amber-100 text-amber-700' },
    { label: 'Avg Confidence', value: `${(summary.avg_confidence * 100).toFixed(0)}%`, color: 'bg-purple-100 text-purple-700' },
    { label: 'Avg Response Time', value: `${(summary.avg_response_time_ms / 1000).toFixed(1)}s`, color: 'bg-blue-100 text-blue-700' },
    { label: 'Satisfaction Rate', value: `${(summary.satisfaction_rate * 100).toFixed(0)}%`, color: 'bg-green-100 text-green-700' },
  ];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Performance metrics from the last 30 days.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="border border-gray-200 rounded-lg p-4 bg-white">
            <p className="text-sm text-gray-500">{stat.label}</p>
            <p className={`text-2xl font-bold mt-1 ${stat.color.split(' ')[1]}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Feedback */}
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <h3 className="font-medium text-gray-900 mb-3">Customer Feedback</h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-gray-700">{summary.satisfaction_positive} helpful</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-gray-700">{summary.satisfaction_negative} not helpful</span>
          </div>
        </div>
      </div>

      {/* Top Escalation Reasons */}
      {summary.top_escalation_reasons.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h3 className="font-medium text-gray-900 mb-3">Top Escalation Reasons</h3>
          <div className="space-y-2">
            {summary.top_escalation_reasons.map((reason, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{reason.reason}</span>
                <span className="font-medium text-gray-900">{reason.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =======================
// HELPERS
// =======================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
    </div>
  );
}
